import { useEffect, useState } from 'react';
import { Cell } from '@table-library/react-table-library/table';
import Actions from './actions';
import moment from 'moment';
import EditProject from './modal/editProject';
import { AI_TOOLS } from '../configs';
import { CircleFlag } from 'react-circle-flags';
import { FREQUENCY_OPTIONS } from '../create/utils';

export const COLUMNS = (t, getKeywords, projects) => [
    {
        label: t('ai_tracking.keyword_manager.table.keyword'),
        key: 'prompt',
        data: 'prompt',
        Component: Cell,
        cellSize: 'minmax(180px, 1.5fr)',
        sortType: 'string',
        renderCell: ({ data, item, csvDownload }) => {
            if (csvDownload) return data;
            return (
                <a
                    className={`ai-tracking-prompt-link ${
                        item.status === 'inactive' ? 'inactive' : ''
                    }`}
                    href={`/ai-tracking/project/${item.project.id}?prompts=${item.id}`}
                >
                    {data}{' '}
                    <span style={{ fontSize: '20px', marginLeft: '3px' }}>
                        <i className="icon-arrow-right1 fs-14" />
                    </span>
                </a>
            );
        },
    },
    {
        label: (
            <div className="ai-tracking-country-language">
                <div className="ai-tracking-country-language-country">
                    {t('ai_tracking.keyword_manager.table.country')}
                </div>
                <div className="ai-tracking-country-language-lan">
                    ({t('ai_tracking.keyword_manager.table.language')})
                </div>
            </div>
        ),
        key: 'country_language',
        data: 'country_language',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return `${item.country} - ${item.language}`;
            return (
                <div className="ai-tracking-country-language d-flex align-items-center gap-4">
                    {item.country && (
                        <CircleFlag
                            countryCode={item.country.toLowerCase()}
                            height="22"
                            width="35"
                        />
                    )}
                    <div className="ai-tracking-country-language-country">{item.country}</div>
                    <div className="ai-tracking-country-language-lan">({item.language})</div>
                </div>
            );
        },
        sortType: (array) =>
            array.sort((a, b) =>
                `${a.country} - ${a.language}`.localeCompare(`${b.country} - ${b.language}`),
            ),
    },
    {
        label: t('ai_tracking.keyword_manager.table.platform'),
        key: 'platform',
        data: 'platform',
        Component: Cell,
        sortType: 'string',
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return item?.platforms?.join(', ');
            return (
                <div className="d-flex flex-wrap gap-4">
                    {Object.keys(AI_TOOLS).map((state) => (
                        <a
                            key={state}
                            title={AI_TOOLS[state]}
                            className={`ai-tracking-platform-2 ${
                                item?.platforms?.includes(state) ? '' : 'disabled'
                            }`}
                            href={`/ai-tracking/project/${item.project.id}?prompts=${item.id}&platforms=${state}`}
                            style={{
                                pointerEvents: item?.platforms?.includes(state) ? 'auto' : 'none',
                            }}
                        >
                            {showPlatform(state)}
                        </a>
                    ))}
                </div>
            );
        },
    },
    {
        label: t('ai_tracking.keyword_manager.table.tags'),
        key: 'tags',
        data: 'tags',
        Component: Cell,
        sortType: 'string',
        cellSize: 'minmax(180px, 1.5fr)',
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data?.join(', ');
            return (
                <div className="d-flex align-items-center w-100 gap-8 flex-wrap">
                    {!data?.length
                        ? '-'
                        : data?.map((tag, index) => (
                              <div key={index} className="ai-tracking-tag">
                                {tag}
                            </div>
                          ))}
                </div>
            );
        },
    },
    {
        label: t('ai_tracking.keyword_manager.table.source'),
        key: 'source',
        data: 'source',
        Component: Cell,
        sortType: 'string',
    },
    {
        label: t('ai_tracking.keyword_manager.table.frequency'),
        key: 'frequency',
        data: 'frequency',
        Component: Cell,
        sortType: 'string',
        cellSize: 'minmax(120px, 1fr)',
        renderCell: ({ data, csvDownload }) => {
            const label =
                FREQUENCY_OPTIONS(t).find((option) => option.value === data)?.label ?? data;
            if (csvDownload) return label;
            return (
                <div className="d-flex align-items-center w-100 gap-8">
                    {data === 'weekly' ? (
                        <img title={label} className="frequency-img" src="/static/img/weekly.svg" />
                    ) : (
                        <img title={label} className="frequency-img" src="/static/img/daily.svg" />
                    )}
                    {label}
                </div>
            );
        },
    },
    {
        label: t('ai_tracking.keyword_manager.table.lastRun'),
        key: 'last_crawled',
        data: 'last_crawled',
        Component: Cell,
        sortType: 'string',
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;
            if (!data) return '-';
            const date = moment(data).format('YYYY-MM-DD');
            const time = moment(data).format('HH:mm');
            return (
                <div className="ai-tracking-country-language">
                    <div className="ai-tracking-country-language-country">{date}</div>
                    <div className="ai-tracking-country-language-lan">({time})</div>
                </div>
            );
        },
    },
    {
        label: t('ai_tracking.keyword_manager.table.projectName'),
        key: 'project_name',
        data: 'project_name',
        cellSize: 'minmax(180px, 1.5fr)',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return item.project.name;
            return (
                <div className="ai-tracking-project-column">
                    <a
                        className={item.project?.is_disabled ? 'inactive-project' : ''}
                        href={`/ai-tracking/project/${item.project.id}`}
                    >
                        {item.project.name}
                        <i className="icon-arrow-right1 fs-14" />
                    </a>
                    <EditProject item={item} projects={projects} getKeywords={getKeywords} />
                </div>
            );
        },
        sortType: (array) => array.sort((a, b) => a.project.name.localeCompare(b.project.name)),
    },
    {
        label: t('ai_tracking.keyword_manager.table.actions'),
        key: 'actions',
        data: 'actions',
        cellSize: 'minmax(180px, 1.5fr)',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return null;
            return (
                <div className="ai-tracking-action">
                    <Actions item={item} status={item.status} getKeywords={getKeywords} />
                </div>
            );
        },
    },
];

const PLATFORM_ICONS = {
    google_ai_overview: { light: '/static/img/google-ai-overview-5.png' },
    grok: { light: '/static/img/grok.png', dark: '/static/img/grok-dark.svg' },
    perplexity: { light: '/static/img/perplexity.png', dark: '/static/img/perplexity-dark.svg' },
    chat_gpt: { light: '/static/img/chatgpt-icon.png', dark: '/static/img/chat-gpt-dark.svg' },
    gemini: { light: '/static/img/google-gemini-icon-2.webp' },
    google_ai_mode: { light: '/static/img/google-ai-mode.svg' },
    google_search: { light: '/static/img/google-icon.png' },
};

function PlatformIcon({ value, style = {} }) {
    const icons = PLATFORM_ICONS[value];
    const [isDark, setIsDark] = useState(() => JSON.parse(localStorage.getItem('dark-mode')));

    useEffect(() => {
        const syncMood = () => setIsDark(JSON.parse(localStorage.getItem('dark-mode')));
        document.addEventListener('changeMoodEvent', syncMood);
        return () => document.removeEventListener('changeMoodEvent', syncMood);
    }, []);

    if (!icons) return null;
    return <img src={isDark && icons.dark ? icons.dark : icons.light} style={style} />;
}

export function showPlatform(value, style = {}) {
    return <PlatformIcon value={value} style={style} />;
}
